import type { TFunction } from "i18next";
import { type DateTime, Settings } from "luxon";
import { type OvertimeContribution, RateHoursBonusType } from "../../utils/models";
import { formatCurrency } from "../../utils/utilFunctions";
import { formatClockTime, formatHoursNumber } from "./consts";
import { audienceParts, percentOf, rolesPart, SCOPE_SEPARATOR } from "./scopeText";
import type { OvertimeBand } from "./wageComposition";

/** DAT-906 · Why an overtime band is overtime, in the rule's own terms, then which staff the rule covers. */

/** Sets a weekly band's cause apart from the limit it explains: "… @ 150% - 40 h reached Sunday". */
const CAUSE_SEPARATOR = " - ";
const DAYS_IN_WEEK_AFTER_START = 6;

const overtimeRate = (t: TFunction, { valueType, value }: OvertimeContribution): string =>
	valueType === RateHoursBonusType.Percentage
		? t("wageBreakdown.overtime.atPercent", { percent: percentOf(value) })
		: t("wageBreakdown.overtime.atFlat", { amount: formatCurrency(value) });

/**
 * A weekday in the language on screen. A DateTime keeps the locale it was created with, and a loaded
 * breakdown outlives a language switch; `i18n.ts` keeps `Settings.defaultLocale` on the current one.
 */
const weekdayIn = (day: DateTime, width: "short" | "long"): string => day.setLocale(Settings.defaultLocale).toLocaleString({ weekday: width });

/**
 * Weekly OT is caused by hours worked before this band — often on another day — which nothing else on
 * screen shows: the day the week reached the limit when that was before this shift, and which days the
 * payroll week spans.
 */
const weeklyCause = (t: TFunction, c: OvertimeContribution): string[] => [
	...(c.limitReachedAt
		? [t("wageBreakdown.overtime.weeklyReached", { hours: formatHoursNumber(c.thresholdHours), day: weekdayIn(c.limitReachedAt, "long") })]
		: []),
	...(c.weekStart
		? [
				t("wageBreakdown.overtime.payrollWeek", {
					from: weekdayIn(c.weekStart, "short"),
					to: weekdayIn(c.weekStart.plus({ days: DAYS_IN_WEEK_AFTER_START }), "short"),
				}),
			]
		: []),
];

/**
 * A weekly limit reached before the shift puts the whole shift past it; one crossed during the shift says
 * from when; with neither known (a line stored before this was captured) it is named without either.
 */
const weeklyLimit = (t: TFunction, c: OvertimeContribution, band: OvertimeBand, hours: string): string => {
	if (c.limitReachedAt) return t("wageBreakdown.overtime.weeklyWholeShift", { hours });
	if (band.crossedAt) return t("wageBreakdown.overtime.weeklyFrom", { hours, time: formatClockTime(band.crossedAt) });
	return t("wageBreakdown.overtime.weeklyInWeek", { hours });
};

const consecutiveCause = (t: TFunction, { scope }: OvertimeContribution): string => {
	if (scope.consecutiveDaysFrom !== null && scope.consecutiveDaysTo !== null)
		return t("wageBreakdown.overtime.consecutiveRange", { from: scope.consecutiveDaysFrom, to: scope.consecutiveDaysTo });
	if (scope.consecutiveDaysFrom !== null) return t("wageBreakdown.overtime.consecutiveFrom", { from: scope.consecutiveDaysFrom });
	return t("wageBreakdown.overtime.consecutive");
};

/** Which limit the band is past: "hours past 12 h", "whole shift is past 40 h", "past 40 h from 2:30 PM". */
const overtimeLimit = (t: TFunction, c: OvertimeContribution, band: OvertimeBand): string => {
	const hours = formatHoursNumber(c.thresholdHours);
	switch (c.otKind) {
		case "daily":
			return t("wageBreakdown.overtime.daily", { hours });
		case "weekly":
			return weeklyLimit(t, c, band, hours);
		case "consecutive":
			return consecutiveCause(t, c);
	}
};

export const overtimeDetail = (t: TFunction, c: OvertimeContribution, band: OvertimeBand): string => {
	const cause = c.otKind === "weekly" ? weeklyCause(t, c) : [];
	const limit = `${overtimeLimit(t, c, band)} ${overtimeRate(t, c)}`;
	const why = cause.length > 0 ? `${limit}${CAUSE_SEPARATOR}${cause.join(", ")}` : limit;
	return [why, ...rolesPart(t, c.scope.roles), ...audienceParts(t, c.scope)].join(SCOPE_SEPARATOR);
};
